/* eslint-disable @typescript-eslint/naming-convention */
import MultiSelectInputMUI from '@brands/Components/Inputs/MuiMultiSelectInput/MultiSelectInputMUI';
import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import ProfileImage, {
  type ProfileImageErrors,
  type ProfileImageGivingUserProfile,
} from '@brands/Components/ProfileImage/ProfileImage';
import AddEmailModal from '@brands/Dashboard/Dashboard/SuperAdmin/Components/AddEmailModal/AddEmailModal';
import ChangePasswordModal from '@brands/Dashboard/Sidenav/SidenavModals/ChangePasswordModal/ChangePasswordModal';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useDivisionScopeActor } from '@brands/hooks/useDivisionScopeActor';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { RegisterMediaPayload } from '@brands/services/communication/registersConversationMedia';
import { activateUserById } from '@brands/services/identity/activateUserById';
import { confirmAvatarMedia } from '@brands/services/identity/confirmAvatarMedia';
import { createBasicInformation } from '@brands/services/identity/createBasicInformation';
import { deleteAvatar } from '@brands/services/identity/deleteAvatar';
import { getCurrentProfile } from '@brands/services/identity/getCurrentProfile';
import { getUserAvatar } from '@brands/services/identity/getUserAvatar';
import { getUserById } from '@brands/services/identity/getUserById';
import { registerAvatarMedia } from '@brands/services/identity/registerAvatarMedia';
import { reinviteUserById } from '@brands/services/identity/reinviteUserById';
import { saveBasicInformation } from '@brands/services/identity/saveBasicInformation';
import { updateMe } from '@brands/services/identity/updateMe';
import { updateUserClientTag } from '@brands/services/identity/updateUserClientTag';
import { getUserClientTagRefs, toLockedSelectOptions } from '@brands/Utils/userClientTags';
import { updateVerifiedEmail } from '@brands/services/identity/updateUserEmail';
import { BasicInfo, ClientTag, UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import {
  ClientDivisionGroup,
  getAllowedDivisionIds,
  getClientDivisionOptionGroups,
  hasUnfilledRequiredDivisionGroup,
  scopeDivisionGroupsToClientAdmin,
} from '@brands/services/identity/clientDivisionOptions';
import { selectAuth } from '@brands/store/selectors/auth';
import { setUser } from '@brands/store/slices/authSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import { yupResolver } from '@hookform/resolvers/yup';
import HttpsOutlinedIcon from '@mui/icons-material/HttpsOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import ConfirmationModal from '@brands/Components/Modals/ConfirmationModal/ConfirmationModal';
import useUnlockAccount from '@brands/Dashboard/MyProfile/Components/useUnlockAccount';
import { Alert, Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { E164Number, parsePhoneNumberFromString } from 'libphonenumber-js';
import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IoAdd, IoChevronBack, IoChevronDown, IoEllipsisVertical } from 'react-icons/io5';
import { toast } from 'react-toastify';

import { CollectorMyProfileSubmit } from './utils/types';
import { collectorProfileValidationSchema } from './utils/validationSchema';
import addNewUserStyles from '../../Dashboard/Collector/AddNewUser/addNewUser.module.scss';
import collectorShellStyles from '../../Dashboard/Collector/EditUser/collectorEditUser.module.scss';
import providerStyles from '../Provider/styles.module.scss';
import styles from '../../Dashboard/SuperAdmin/AddUserPages/styles.module.scss';

export type ClientTagOption = { id: string; value: number; label: string };

function hasProfilePictureMedia(user: UserProfile): boolean {
  const raw = user.picture_media_id as string | number | undefined | null;
  if (raw == null) return false;
  const s = String(raw).trim();
  return s !== '' && s !== '0';
}

/** E.164 in form state — matches Provider / Care Advisor profile + MuiTelInput. */
function normalizePhoneToE164ForForm(phone: string | undefined): string {
  if (!phone?.trim()) return '';
  const us = parsePhoneNumberFromString(phone, 'US');
  if (us?.isValid()) return us.format('E.164');
  const intl = parsePhoneNumberFromString(phone);
  if (intl?.isValid()) return intl.format('E.164');
  return phone;
}

const mapUserToCollectorForm = (user: UserProfile): CollectorMyProfileSubmit => {
  const uid = Number(user.id) || 0;
  const legacyAvatarUrl = (user as UserProfile & { avatar?: { url?: string } }).avatar?.url;
  const profile_img = legacyAvatarUrl || (hasProfilePictureMedia(user) && uid ? getUserAvatar(uid) : '');

  return {
    collectorId: uid,
    profile_img,
    first_name: user.basic_info?.first_name || '',
    middle_name: user.basic_info?.middle_name || '',
    last_name: user.basic_info?.last_name || '',
    email: user.email || '',
    phone_number: normalizePhoneToE164ForForm(user.phone_number),
    client_tags: (user.clients || []).map((cli) => ({
      id: cli.id.toString(),
      value: cli.id,
      label: cli.name,
    })),
    client_divisions: (user.divisions || []).map((d) => ({
      id: d.id.toString(),
      value: d.id,
      label: d.name,
    })),
    organizations:
      user.organizations?.map((org) => {
        const matchingDivision = user.organization_admin_profile?.divisions?.find(
          (division) => division?.organization_id === org.id,
        );

        const matchingPractice = user.organization_admin_profile?.practices?.find(
          (practice) => practice?.organization_id === org.id,
        );

        return {
          ...org,
          divisions: matchingDivision ? [matchingDivision] : [],
          practices: matchingPractice ? [matchingPractice] : [],
        };
      })[0] || {},
  };
};

function collectorBasicInfoChanged(user: UserProfile, first: string, middle: string, last: string): boolean {
  const b = user.basic_info;
  return (
    (b?.first_name || '') !== (first || '') ||
    (b?.middle_name || '') !== (middle || '') ||
    (b?.last_name || '') !== (last || '')
  );
}

function collectorClientTagsChanged(user: UserProfile, client_tags: number[]): boolean {
  const existing = (user.clients || []).map((c) => c.id).sort();
  const next = [...client_tags].sort();
  return !isEqual(existing, next);
}

function collectorClientDivisionsChanged(user: UserProfile, client_division_ids: number[]): boolean {
  const existing = (user.divisions || []).map((d) => d.id).sort();
  const next = [...client_division_ids].sort();
  return !isEqual(existing, next);
}
const CollectorProfile = ({
  formRef,
  setIsDirty,
  myProfileForm,
  setMyProfileForm,
  thisUser,
  setThisUser,
  setCurrentAvatarURL,
  clientTagOptions,
  clientTagCatalog = [],
  reviewProfile = false,
  resetEmailSent = false,
  setResetPasswordConfirmation,
  setChangeUserStatusFlag,
  userId,
}: {
  formRef: React.RefObject<HTMLFormElement>;
  setIsDirty: (value: boolean) => void;
  myProfileForm: CollectorMyProfileSubmit;
  setMyProfileForm: React.Dispatch<React.SetStateAction<CollectorMyProfileSubmit | null>>;
  thisUser: UserProfile;
  setThisUser: React.Dispatch<React.SetStateAction<UserProfile>>;
  setCurrentAvatarURL: React.Dispatch<React.SetStateAction<string>>;
  clientTagOptions: ClientTagOption[];
  clientTagCatalog?: ClientTag[];
  reviewProfile?: boolean;
  resetEmailSent?: boolean;
  setResetPasswordConfirmation?: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      userState: string;
    }>
  >;
  setChangeUserStatusFlag?: React.Dispatch<React.SetStateAction<string>>;
  userId?: string;
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector(selectAuth);
  const divisionScopeActor = useDivisionScopeActor();
  const {
    showUnlockAccount,
    isUnlocking,
    unlockConfirmOpen,
    openUnlockConfirm,
    closeUnlockConfirm,
    handleUnlockAccount,
  } = useUnlockAccount(thisUser, setThisUser);
  const navigate = useRoleNavigate();
  const styles = providerStyles;
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const [resetPasswordModal, setResetPasswordModal] = useState(false);
  const [userHeaderMenuAnchor, setUserHeaderMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [isInvitedUser, setIsInvitedUser] = useState(false);
  const [displayEmailModal, setDisplayEmailModal] = useState(false);
  const [image, setImage] = useState<string>();
  const [avatarPayload, setAvatarPayload] = useState<RegisterMediaPayload | null>(null);
  const [avatarAction, setAvatarAction] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const validationSchema = useMemo(() => collectorProfileValidationSchema(reviewProfile), [reviewProfile]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    getValues,
    formState: { errors },
    reset,
  } = useForm<CollectorMyProfileSubmit>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: myProfileForm,
  });

  const firstNameW = watch('first_name');
  const middleNameW = watch('middle_name');
  const lastNameW = watch('last_name');
  const emailW = watch('email');
  const phoneW = watch('phone_number');
  const currentTags = watch('client_tags');
  const currentDivisions = watch('client_divisions');

  const divisionGroups = useMemo(
    () =>
      scopeDivisionGroupsToClientAdmin(
        getClientDivisionOptionGroups(
          clientTagCatalog,
          (currentTags || []).map((t) => t.value),
          currentDivisions || [],
        ),
        clientTagCatalog,
        divisionScopeActor,
      ),
    [clientTagCatalog, currentTags, currentDivisions, divisionScopeActor],
  );

  // A Client Admin never changes a user's client tags, so the tag field is read-only for them and
  // the save sends the tags back as the server holds them; divisions under their own tag stay editable.
  const clientTagsLocked = Boolean(reviewProfile) && userInfo.role.name === UserRoleName.OrganizationAdmin;
  const lockedClientTagOptions = useMemo(() => toLockedSelectOptions(getUserClientTagRefs(thisUser)), [thisUser]);

  // Each picker owns only its own tag's divisions; the rest of the field rides along.
  const setGroupDivisions = (group: ClientDivisionGroup, ids: string[]): void => {
    const groupIds = new Set(group.options.map((option) => String(option.id)));

    setValue(
      'client_divisions',
      [
        ...(currentDivisions || []).filter((division) => !groupIds.has(String(division.id))),
        ...group.options.filter((option) => ids.includes(option.id)),
      ],
      { shouldValidate: true },
    );
  };
  const hasFormDirtyVsServer = useMemo(() => {
    if (!reviewProfile || !myProfileForm) return false;
    return (
      (firstNameW || '') !== (myProfileForm.first_name || '') ||
      (middleNameW || '') !== (myProfileForm.middle_name || '') ||
      (lastNameW || '') !== (myProfileForm.last_name || '') ||
      (emailW || '').trim() !== (myProfileForm.email || '').trim() ||
      (phoneW || '') !== (myProfileForm.phone_number || '') ||
      !isEqual(
        (currentTags || []).map((t) => t.value).sort(),
        (myProfileForm.client_tags || []).map((t) => t.value).sort(),
      ) ||
      !isEqual(
        (currentDivisions || []).map((d) => d.value).sort(),
        (myProfileForm.client_divisions || []).map((d) => d.value).sort(),
      )
    );
  }, [reviewProfile, myProfileForm, firstNameW, middleNameW, lastNameW, emailW, phoneW, currentTags, currentDivisions]);

  const hasUnsavedChanges = useMemo(() => {
    if (reviewProfile) {
      return avatarAction !== '' || hasFormDirtyVsServer;
    }
    return avatarAction !== '';
  }, [reviewProfile, avatarAction, hasFormDirtyVsServer]);

  useEffect(() => {
    if (myProfileForm && Object.keys(myProfileForm).length > 0) {
      reset(structuredClone(myProfileForm));
    }
  }, [myProfileForm, reset]);

  useEffect(() => {
    setIsDirty(hasUnsavedChanges);
  }, [hasUnsavedChanges, setIsDirty]);

  const triggerProfileAvatar = useCallback(
    (avatarUrl: string) => {
      setValue('profile_img', avatarUrl || '', { shouldValidate: true });
    },
    [setValue],
  );

  const onInvalid = (err: Record<string, unknown>): void => {
    const first = Object.values(err)[0] as { message?: string } | undefined;
    if (first?.message) {
      showSnackbar(first.message, 'error');
    }
  };

  const normalizePhoneForCompare = (phone: string | undefined): string => normalizePhoneToE164ForForm(phone);

  const onSubmit = async (data: CollectorMyProfileSubmit): Promise<void> => {
    const isCollectorSelfEdit =
      userInfo.role.name === UserRoleName.Collector && Number(thisUser.id) === Number(userInfo.id);
    if (
      reviewProfile &&
      hasUnfilledRequiredDivisionGroup(
        divisionGroups,
        (data.client_divisions || []).map((division) => division.id),
      )
    ) {
      showSnackbar('Select a division.', 'warning');
      return;
    }
    if (reviewProfile) {
      setIsSavingProfile(true);
      try {
        const uid = Number(thisUser.id);

        if (avatarPayload && avatarFile) {
          const res = await registerAvatarMedia(avatarPayload);
          const mediaId = res.files[0].upload_id;
          await fetch(res.files[0].upload_url, {
            method: 'PUT',
            body: avatarFile,
            headers: { 'content-type': avatarFile.type },
          });
          await confirmAvatarMedia(mediaId, uid);
          triggerProfileAvatar(res.files[0].upload_url);
          setAvatarFile(null);
          setAvatarPayload(null);
          setAvatarAction('');
        } else if (avatarAction === 'Remove') {
          await deleteAvatar(uid);
          triggerProfileAvatar('');
          setAvatarAction('');
        }

        if (!isCollectorSelfEdit) {
          const nextEmail = (data.email || '').trim();
          if ((thisUser.email || '').trim() !== nextEmail) {
            await updateVerifiedEmail(uid, { email: nextEmail });
          }
        }

        const basicPayload: BasicInfo = {
          ...(thisUser.basic_info || {}),
          first_name: data.first_name || '',
          middle_name: data.middle_name || '',
          last_name: data.last_name || '',
          languages: thisUser.basic_info?.languages?.map((lang) =>
            typeof lang === 'string' ? lang : lang.id.toString(),
          ),
        };
        if (
          !isCollectorSelfEdit &&
          collectorBasicInfoChanged(thisUser, data.first_name, data.middle_name, data.last_name)
        ) {
          if (thisUser.basic_info != null) {
            await saveBasicInformation(basicPayload, uid);
          } else {
            await createBasicInformation(basicPayload, uid);
          }
        }

        const normalizedFormPhone = normalizePhoneForCompare(data.phone_number);
        const normalizedUserPhone = normalizePhoneForCompare(thisUser.phone_number);
        if (!isCollectorSelfEdit && (normalizedFormPhone || '') !== (normalizedUserPhone || '')) {
          const patchPhone = data.phone_number?.trim()
            ? parsePhoneNumberFromString(data.phone_number, 'US')?.format('E.164') ||
              parsePhoneNumberFromString(data.phone_number)?.format('E.164') ||
              data.phone_number
            : null;
          await updateMe({ phone_number: patchPhone }, uid, userInfo.role.name);
        }
        const nextClientTagValues = (data.client_tags || []).map((t) => t.value);
        const nextClientDivisionValues = (data.client_divisions || []).map((d) => d.value);
        const tagsChanged = collectorClientTagsChanged(thisUser, nextClientTagValues);
        const divisionsChanged = collectorClientDivisionsChanged(thisUser, nextClientDivisionValues);
        // A locked user's tags go back exactly as they hold them: the server lets an unchanged set
        // through and scopes only the divisions this save changes.
        if (!isCollectorSelfEdit && (clientTagsLocked ? divisionsChanged : tagsChanged || divisionsChanged)) {
          const client_ids = clientTagsLocked
            ? (thisUser.clients || []).map((c) => c.id)
            : (data.client_tags || []).map((t) => t.id);
          const client_division_ids = (data.client_divisions || []).map((d) => d.id);
          await updateUserClientTag(uid, { client_ids, client_division_ids });
        }
        const fresh = await getUserById(uid, userInfo.role.name);
        setThisUser(fresh);
        setMyProfileForm(mapUserToCollectorForm(fresh));
        setAvatarAction('');
        setAvatarFile(null);
        setAvatarPayload(null);
        setImage(undefined);

        showSnackbar('Your changes have been saved.', 'success');
      } catch (e: unknown) {
        displayErrorDetails(e);
      } finally {
        setIsSavingProfile(false);
      }
      return;
    }

    setIsSavingProfile(true);
    try {
      if (avatarPayload && avatarFile) {
        const res = await registerAvatarMedia(avatarPayload, userInfo.role.name);
        const mediaId = res.files[0].upload_id;
        await fetch(res.files[0].upload_url, {
          method: 'PUT',
          body: avatarFile,
          headers: { 'content-type': avatarFile.type },
        });
        await confirmAvatarMedia(mediaId, Number(thisUser.id), undefined, userInfo.role.name, {
          collectorEditingSelf: true,
        });
        setAvatarFile(null);
        setAvatarPayload(null);
        setAvatarAction('');
      } else if (avatarAction === 'Remove') {
        await deleteAvatar(Number(thisUser.id));
        triggerProfileAvatar('');
        setAvatarAction('');
        setImage(undefined);
      }

      const fresh = await getCurrentProfile(userInfo.role.name);
      setThisUser(fresh);
      dispatch(setUser({ userInfo: fresh }));

      const mapped = mapUserToCollectorForm(fresh);
      setMyProfileForm(mapped);
      reset(structuredClone(mapped));
      setAvatarAction('');

      showSnackbar('Your changes have been saved.', 'success');
    } catch (e: unknown) {
      displayErrorDetails(e);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const disabledSave = !hasUnsavedChanges || isSavingProfile;

  const fieldsDisabled = !reviewProfile;

  const profileImgFromForm = watch('profile_img');
  const profileImageSrc = image || profileImgFromForm || undefined;

  const headerDisplayName = useMemo(() => {
    return (
      thisUser.full_name?.trim() ||
      [thisUser.basic_info?.first_name, thisUser.basic_info?.last_name].filter(Boolean).join(' ').trim() ||
      '—'
    );
  }, [thisUser.basic_info?.first_name, thisUser.basic_info?.last_name, thisUser.full_name]);

  const displayStatus = useMemo(() => {
    if ((thisUser as { deleted_at?: string })?.deleted_at) return 'Deactivated';
    return thisUser.status?.trim() || 'Active';
  }, [thisUser]);

  const statusPillClass =
    displayStatus === 'Active'
      ? collectorShellStyles.statusPillReadOnlyActive
      : displayStatus === 'Deactivated'
      ? collectorShellStyles.statusPillReadOnlyDeactivated
      : collectorShellStyles.statusPillReadOnlyOther;

  const statusOptions = useMemo(() => {
    if (
      thisUser.role.name === UserRoleName.Provider &&
      displayStatus !== 'Pending' &&
      displayStatus !== 'Deactivated'
    ) {
      return [
        { id: 'pending', name: 'Pending' },
        { id: 'deactivated', name: 'Deactivated' },
      ];
    }
    if (thisUser.role.name === UserRoleName.Provider && displayStatus === 'Pending') {
      return [
        { id: 'active', name: 'Active' },
        { id: 'deactivated', name: 'Deactivated' },
      ];
    }
    if (displayStatus !== 'Deactivated') {
      return [{ id: 'deactivated', name: 'Deactivated' }];
    }
    return [{ id: 'active', name: 'Active' }];
  }, [displayStatus, thisUser.role.name]);

  const canCreateCase = thisUser.role.name === UserRoleName.Patient || thisUser.role.name === UserRoleName.Dependent;

  const openUserHeaderMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setUserHeaderMenuAnchor(event.currentTarget);
  };

  const closeUserHeaderMenu = (): void => {
    setUserHeaderMenuAnchor(null);
  };

  const openStatusMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setStatusMenuAnchor(event.currentTarget);
  };

  const closeStatusMenu = (): void => {
    setStatusMenuAnchor(null);
  };

  const getInvitationDomain = (): string => {
    const org =
      thisUser.organizations.find((organization) => organization.id !== 'dentistry-one') ??
      thisUser.organizations.find((organization) => organization.id === 'dentistry-one');
    const currentDomain = window.location.hostname;
    const fallbackDomain =
      currentDomain === 'localhost' || currentDomain.startsWith('localhost') ? 'develop.dentistry.one' : currentDomain;

    if (!org) return fallbackDomain;

    const divisionDomain = org.divisions?.[0]?.domain;
    const practiceDomain = org.practices?.[0]?.domain;

    return divisionDomain || practiceDomain || fallbackDomain;
  };

  const inviteUser = async (): Promise<void> => {
    if (!userId) return;
    setIsInvitedUser(true);
    try {
      const updatedUser = await reinviteUserById(
        userId,
        { frontend_website: `https://${getInvitationDomain()}` },
        userInfo.role.name,
      );
      setThisUser({ ...thisUser, status: updatedUser?.status, email: updatedUser?.email });
    } catch (error: unknown) {
      const responseMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      if (responseMessage?.includes('User has no email') || responseMessage?.includes('12209-3')) {
        setDisplayEmailModal(true);
        return;
      }
      displayErrorDetails(error);
    } finally {
      setIsInvitedUser(false);
    }
  };

  const handleStatusClick = async (status: string): Promise<void> => {
    if (status === 'deactivated' || status === 'pending') {
      setChangeUserStatusFlag?.(status);
    } else if (userId) {
      try {
        const userProfile = await activateUserById(Number(userId));
        setThisUser({ ...thisUser, status: userProfile?.status ?? thisUser.status, deleted_at: null });
        toast.success('User has been activated.', { className: 'fs-unmask' });
      } catch (error: unknown) {
        displayErrorDetails(error);
      }
    }
    closeStatusMenu();
  };

  return (
    <div className={`${styles.ProfileContainer}${!reviewProfile ? ` ${styles.ProfileContainerOwnProfile}` : ''}`}>
      {reviewProfile && (
        <div className={styles.reviewHeaderShell}>
          <button type="button" className={styles.reviewBackButton} onClick={() => navigate(-1)}>
            <IoChevronBack aria-hidden />
            Back
          </button>

          <header className={`${collectorShellStyles.collectorEditUserHeader} ${styles.reviewHeader}`}>
            <div className={collectorShellStyles.collectorEditUserHeaderIdentity}>
              <ProfileImage
                className={styles.headerProfileImage}
                reviewProfile={reviewProfile}
                id={String(myProfileForm.collectorId)}
                setCurrentAvatarURL={setCurrentAvatarURL}
                setAvatarFile={setAvatarFile}
                setAvatarPayload={setAvatarPayload}
                setAvatarAction={setAvatarAction}
                triggerProfileAvatar={triggerProfileAvatar}
                setImage={setImage}
                image={profileImageSrc}
                errors={errors as ProfileImageErrors}
                size="80px"
                disableDelete
                givingUserProfile={
                  {
                    first_name: thisUser.basic_info?.first_name || headerDisplayName.split(' ')[0] || '',
                    last_name: thisUser.basic_info?.last_name || headerDisplayName.split(' ').slice(1).join(' ') || '',
                  } as ProfileImageGivingUserProfile
                }
              />
              <div className={`${collectorShellStyles.collectorEditUserHeaderText} ${styles.reviewHeaderText}`}>
                <h1 className={`${collectorShellStyles.collectorEditUserHeaderName} ${styles.reviewHeaderName}`}>
                  {headerDisplayName}
                </h1>
                <div className={`${collectorShellStyles.userStatusRow} ${styles.reviewStatusRow}`}>
                  <span className={`${collectorShellStyles.userStatusLabel} ${styles.reviewStatusLabel}`}>
                    User status:
                  </span>
                  <button
                    type="button"
                    className={`${collectorShellStyles.statusPillReadOnly} ${statusPillClass} ${styles.statusPillButton}`}
                    aria-controls={statusMenuAnchor ? 'collector-review-user-status-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={statusMenuAnchor ? 'true' : undefined}
                    onClick={openStatusMenu}
                  >
                    {displayStatus}
                    <IoChevronDown aria-hidden />
                  </button>
                  <Menu
                    id="collector-review-user-status-menu"
                    anchorEl={statusMenuAnchor}
                    open={Boolean(statusMenuAnchor)}
                    onClose={closeStatusMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    {statusOptions.map((status) => (
                      <MenuItem key={status.id} onClick={() => handleStatusClick(status.id)}>
                        <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                          {status.name}
                        </ListItemText>
                      </MenuItem>
                    ))}
                  </Menu>
                </div>
              </div>
            </div>
            <div className={collectorShellStyles.collectorEditUserHeaderActions}>
              {canCreateCase && (
                <button
                  type="button"
                  className={collectorShellStyles.collectorEditUserCreateCaseLink}
                  onClick={() => navigate('/review-profile/patient/select')}
                >
                  <IoAdd aria-hidden />
                  Create new case
                </button>
              )}
              {thisUser.role.name !== UserRoleName.Dependent && (
                <>
                  <IconButton
                    aria-label="More actions"
                    aria-controls={userHeaderMenuAnchor ? 'collector-review-user-header-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={userHeaderMenuAnchor ? 'true' : undefined}
                    onClick={openUserHeaderMenu}
                  >
                    <IoEllipsisVertical />
                  </IconButton>
                  <Menu
                    id="collector-review-user-header-menu"
                    anchorEl={userHeaderMenuAnchor}
                    open={Boolean(userHeaderMenuAnchor)}
                    onClose={closeUserHeaderMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem
                      onClick={() => {
                        closeUserHeaderMenu();
                        void inviteUser();
                      }}
                      disabled={
                        thisUser.role.name === UserRoleName.Dependent ||
                        ['Active', 'Pending', 'Deactivated'].includes(thisUser.status) ||
                        isInvitedUser
                      }
                    >
                      <ListItemIcon>
                        <IoAdd />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                        Invite user
                      </ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        closeUserHeaderMenu();
                        setResetPasswordConfirmation?.({ isOpen: true, userState: 'Active' });
                      }}
                      disabled={['Deactivated', 'Not Invited', 'Invited'].includes(thisUser.status) || resetEmailSent}
                    >
                      <ListItemIcon>
                        <HttpsOutlinedIcon />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                        Reset password
                      </ListItemText>
                    </MenuItem>
                    {showUnlockAccount && (
                      <MenuItem
                        onClick={() => {
                          closeUserHeaderMenu();
                          openUnlockConfirm();
                        }}
                        disabled={!thisUser.email || isUnlocking}
                      >
                        <ListItemIcon>
                          <LockOpenOutlinedIcon />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ fontFamily: 'Poppins, sans-serif', fontSize: 14 }}>
                          Unlock account
                        </ListItemText>
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </div>
          </header>

          <Alert
            severity="info"
            className={addNewUserStyles.patientProfileNotice}
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
          >
            You are editing <strong>{headerDisplayName}</strong>&apos;s profile.
          </Alert>

          {displayEmailModal && userId && (
            <AddEmailModal
              isOpen={displayEmailModal}
              onClose={() => setDisplayEmailModal(false)}
              userId={userId}
              thisUser={thisUser}
              setThisUser={setThisUser}
              inviteUser={inviteUser}
            />
          )}

          <ConfirmationModal
            isOpen={unlockConfirmOpen}
            title="Unlock account"
            content={`Are you sure you want to unlock ${headerDisplayName}'s account? This clears the account-lockout state so they can sign in again.`}
            onClose={closeUnlockConfirm}
            onConfirm={() => void handleUnlockAccount()}
            isSubmitDisabled={isUnlocking}
          />
        </div>
      )}
      <div className={styles.ProfileDiv}>
        {!reviewProfile && (
          <div className={styles.profileAvatar}>
            <ProfileImage
              reviewProfile={reviewProfile}
              id={String(myProfileForm.collectorId)}
              setCurrentAvatarURL={setCurrentAvatarURL}
              setAvatarFile={setAvatarFile}
              setAvatarPayload={setAvatarPayload}
              setAvatarAction={setAvatarAction}
              triggerProfileAvatar={triggerProfileAvatar}
              setImage={setImage}
              image={profileImageSrc}
              errors={errors as ProfileImageErrors}
              size="80px"
            />
          </div>
        )}

        <form ref={formRef} className={styles.formContainer} onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <TextField
                {...register('first_name')}
                label="First name"
                errors={errors.first_name ? errors : {}}
                name="first_name"
                value={watch('first_name')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (errors.first_name) clearErrors('first_name');
                  setValue('first_name', e.target.value, { shouldValidate: true });
                }}
                required
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                disabled={fieldsDisabled}
              />
            </div>
            <div className={styles.inputWrap}>
              <TextField
                {...register('middle_name')}
                label="Middle name (optional)"
                errors={errors.middle_name ? errors : {}}
                name="middle_name"
                value={watch('middle_name')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (errors.middle_name) clearErrors('middle_name');
                  setValue('middle_name', e.target.value, { shouldValidate: true });
                }}
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                disabled={fieldsDisabled}
              />
            </div>
            <div className={styles.inputWrap}>
              <TextField
                {...register('last_name')}
                label="Last name"
                errors={errors.last_name ? errors : {}}
                name="last_name"
                value={watch('last_name')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (errors.last_name) clearErrors('last_name');
                  setValue('last_name', e.target.value, { shouldValidate: true });
                }}
                required
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                disabled={fieldsDisabled}
              />
            </div>
          </div>

          <div className={styles.inputWrapRow}>
            <div className={styles.inputWrap}>
              <TextField
                {...register('email')}
                label="Email address"
                type="email"
                errors={errors.email ? errors : {}}
                name="email"
                value={watch('email')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (errors.email) clearErrors('email');
                  setValue('email', e.target.value, { shouldValidate: true });
                }}
                required
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                disabled={fieldsDisabled}
              />
            </div>
            <div className={`${styles.inputWrap} ${styles.inputWrapPhoneInput}`}>
              <PhoneInput
                value={watch('phone_number') as E164Number}
                id="phone_number"
                name="phone_number"
                onChange={(val: string | undefined) => {
                  if (val === undefined || val === '') {
                    setValue('phone_number', '', { shouldValidate: true });
                    return;
                  }
                  setValue('phone_number', val, { shouldValidate: true });
                }}
                required
                label="Phone number"
                errors={errors}
                inputClass={styles.profileInputClass}
                wrapperClassName={`fs-unmask ${styles.completeProfilePhone}`}
                disabled={fieldsDisabled}
              />
            </div>
          </div>
          {clientTagsLocked && (
            <div className={styles.inputWrapRow}>
              <div className={styles.inputWrap} style={{ flex: '1 1 100%', maxWidth: '100%' }}>
                <MultiSelectInputMUI
                  name="locked_client_tags"
                  label="Client tags"
                  withId
                  disabled
                  options={lockedClientTagOptions}
                  wrapperClassName={styles.inputWrapper}
                  value={lockedClientTagOptions.map((option) => option.id)}
                />
              </div>
            </div>
          )}
          {!clientTagsLocked && (
            <div className={styles.inputWrapRow}>
              <div className={styles.inputWrap} style={{ flex: '1 1 100%', maxWidth: '100%' }}>
                <MultiSelectInputMUI
                  name="client_tags"
                  label="Client tags"
                  required
                  withId
                  options={clientTagOptions}
                  wrapperClassName={styles.inputWrapper}
                  errors={errors}
                  disabled={fieldsDisabled}
                  value={(currentTags || []).map((t) => t.id)}
                  onChange={(ids: string[]) => {
                    const selectedOptions = clientTagOptions.filter((option) => ids.includes(option.id));
                    setValue('client_tags', selectedOptions, { shouldValidate: true });
                    const allowedDivisionIds = getAllowedDivisionIds(
                      clientTagCatalog,
                      selectedOptions.map((option) => option.value),
                      (thisUser.divisions || []).map((d) => ({ id: String(d.id), value: d.id, label: d.name })),
                    );
                    setValue(
                      'client_divisions',
                      (currentDivisions || []).filter((division) => allowedDivisionIds.has(String(division.id))),
                      { shouldValidate: true },
                    );
                  }}
                />
              </div>
            </div>
          )}

          {divisionGroups.map((group) => (
            <div className={styles.inputWrapRow} key={group.clientTagId}>
              <div className={styles.inputWrap} style={{ flex: '1 1 100%', maxWidth: '100%' }}>
                <MultiSelectInputMUI
                  name="client_divisions"
                  label={`${group.clientTagName} - Division(s)`}
                  withId
                  required={group.required}
                  options={group.options}
                  wrapperClassName={styles.inputWrapper}
                  errors={errors}
                  disabled={fieldsDisabled}
                  value={(currentDivisions || [])
                    .filter((division) => group.options.some((option) => option.id === String(division.id)))
                    .map((division) => String(division.id))}
                  onChange={(ids: string[]) => setGroupDivisions(group, ids)}
                />
              </div>
            </div>
          ))}

          <div className={styles.buttonDiv}>
            <Button
              type="submit"
              variant="contained"
              className={`${isSavingProfile ? styles.inActiveBtn : ''} fs-unmask ${styles.formBtn}`}
              style={{ borderRadius: '32px', height: '42px', width: 'auto' }}
              disabled={disabledSave}
            >
              Save changes
            </Button>
            {!reviewProfile && (
              <Button
                variant="text"
                color="error"
                type="button"
                onClick={() => setResetPasswordModal(true)}
                className="fs-unmask"
              >
                Reset password
              </Button>
            )}
          </div>
        </form>

        {!reviewProfile && resetPasswordModal && (
          <ChangePasswordModal
            isOpen={resetPasswordModal}
            onClose={() => setResetPasswordModal(false)}
            email={userInfo?.email || ''}
          />
        )}

        <GenericSnackbar
          open={snackbar.isOpen}
          message={snackbar.message}
          type={snackbar.type}
          onClose={closeSnackbar}
          anchorInDashboardMain
        />
      </div>
    </div>
  );
};

export default CollectorProfile;
export { mapUserToCollectorForm };
